import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// One box that finds a ticket, an asset, an intercom number, a person's work
// email, a policy, a room, a meeting or a project — and takes you to the page
// that can act on it.
//
// Results are scoped on the SERVER to whatever the person may already see, so
// this never becomes a way around campus restrictions. The only thing resolved
// here in the browser is the menu jump list, because a menu the person cannot
// open is not a secret — it simply does not appear for them.
const ICON = { tasks:'✓', assets:'□', rooms:'🚪', containers:'👥', policies:'📋',
               intercom:'☎️', people:'👤', menu:'➜' };

// Every destination the app has, with the permission that reveals it. `perm`
// null means everyone; `roles` pins an entry to particular roles.
const MENU = [
  ['command',       'Command Center',        null, ['admin','it_head']],
  ['dashboard',     'Dashboard',             null],
  ['mypolicies',    'My Policies',           null],
  ['calendar',      'IT Calendar',           null],
  ['training',      'IT Team Training',      null],
  ['tasks',         'Tasks',                 'tasks'],
  ['projects',      'Projects',              'containers'],
  ['meetings',      'Meetings',              'containers'],
  ['meetingpoints', 'Meeting Points',        'containers'],
  ['incidents',     'Incidents',             'incidents'],
  ['problems',      'Problems',              'problems'],
  ['routines',      'Routines',              'routines'],
  ['healthchecks',  'Health Checks',         'healthchecks'],
  ['surveillance',  'Surveillance',          'surveillance'],
  ['remote',        'Remote Support',        'remote_support'],
  ['assets',        'Assets',                'assets'],
  ['consumables',   'Consumables',           'assets'],
  ['scrap',         'Scrap',                 'assets'],
  ['rooms',         'Rooms',                 'rooms'],
  ['configs',       'Device Config',         'configs'],
  ['policies',      'Policies',              'policies'],
  ['documents',     'Documents',             'documents'],
  ['master',        'IT Resources · Intercom & Staff', null],
  ['analytics',     'Analytics',             'analytics'],
  ['performance',   'Performance & Work Report', 'analytics'],
  ['approvals',     'Approval Center',       'approvals'],
  ['announcements', 'Announcements',         'announcements'],
  ['vendormgmt',    'Vendor Management',     'vendors'],
  ['feedash',       'Fee Dashboard',         'fee_dashboard'],
  ['feeupload',     'Fee Report Upload',     'fee_upload'],
  ['mail',          'Mail Center',           'mail'],
  ['feedback',      'Feedback',              'feedback'],
  ['users',         'Users',                 'users'],
  ['roles',         'Roles',                 'roles'],
  ['permissions',   'Permissions',           'permissions'],
  ['backup',        'Backup Management',     'backup'],
  ['logs',          'System Logs',           'logs'],
  ['activity',      'User Activity',         'logs'],
  ['ailogs',        'AI Logs',               'ai'],
  ['recycle',       'Recycle Bin',           'recycle'],
  ['settings',      'Settings',              'settings'],
];

export default function UniversalSearch({ go }) {
  const { user, can } = useAuth();
  const [q, setQ] = useState('');
  const [copied, setCopied] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const away = e => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  // Wait for a pause in typing rather than firing on every keystroke.
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRes(null); setOpen(false); return; }
    // Menu matches are instant, so show the panel while the server answers
    // rather than making the person wait 300ms to see anything at all.
    setOpen(true);
    setBusy(true);
    timer.current = setTimeout(() => {
      api.get('/search', { params: { q: q.trim() } })
        .then(r => { setRes(r.data); setOpen(true); })
        .catch(() => setRes(null))
        .finally(() => setBusy(false));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const openItem = useCallback((group, item) => {
    setOpen(false); setQ('');
    go(item.page || group.page, item.id);
  }, [go]);

  // An intercom number or a work email is usually wanted in the clipboard, not
  // on another page — so those rows offer Copy beside the jump.
  const copy = useCallback((e, value) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(value)
      .then(() => { setCopied(value); setTimeout(() => setCopied(''), 1200); })
      .catch(() => {});
  }, []);

  // Menu matches, worked out in the browser: the same permission test the
  // sidebar uses, so search can only ever offer a page the person could
  // already have clicked to.
  const menuGroup = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return null;
    const items = MENU
      .filter(([, label, perm, roles]) =>
        (!roles || roles.includes(user?.role)) && (!perm || can(perm, 'view')) &&
        label.toLowerCase().includes(term))
      .slice(0, 6)
      .map(([page, label]) => ({ id: page, page, title: label, sub: 'open this page' }));
    return items.length ? { key: 'menu', label: 'Go to', items } : null;
  }, [q, can, user]);

  const groups = res ? [...(menuGroup ? [menuGroup] : []), ...res.groups] : (menuGroup ? [menuGroup] : []);
  const total = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div ref={box} style={{ position:'relative', width:'100%', maxWidth:560 }}>
      <div style={{ position:'relative' }}>
        <span style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)',
          fontSize:14, color:'var(--text2)', pointerEvents:'none' }}>🔍</span>
        <input value={q} onChange={e => setQ(e.target.value)}
          onFocus={() => res && setOpen(true)}
          onKeyDown={e => { if (e.key === 'Escape') { setOpen(false); e.target.blur(); } }}
          placeholder="Search tickets, people, intercom, assets, policies, menus…"
          style={{ width:'100%', paddingLeft:32 }} />
        {q && (
          <button onClick={() => { setQ(''); setRes(null); setOpen(false); }}
            style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
              background:'none', border:'none', cursor:'pointer', color:'var(--text2)', fontSize:15 }}>✕</button>
        )}
      </div>

      {open && (res || menuGroup) && (
        <div className="card" style={{ position:'absolute', left:0, right:0, top:'calc(100% + 6px)',
          maxHeight:420, overflowY:'auto', zIndex:300, padding:0,
          boxShadow:'0 10px 30px rgba(14,27,71,0.18)' }}>
          {!total && (
            <div style={{ padding:'20px 14px', textAlign:'center', fontSize:13, color:'var(--text2)' }}>
              {busy ? 'Searching…' : <>Nothing found for “{q.trim()}”.</>}
            </div>
          )}
          {groups.map(g => (
            <div key={g.key}>
              <div style={{ padding:'7px 12px', fontSize:10.5, fontWeight:700, letterSpacing:'.05em',
                color:'var(--text2)', textTransform:'uppercase', background:'var(--bg3)',
                position:'sticky', top:0 }}>
                {g.label}
              </div>
              {g.items.map(it => (
                <div key={`${g.key}-${it.id}`}
                  style={{ display:'flex', alignItems:'center', gap:6,
                    borderBottom:'1px solid var(--border)' }}>
                  <button onClick={() => openItem(g, it)}
                    style={{ flexGrow:1, minWidth:0, textAlign:'left', border:'none',
                      background:'none', cursor:'pointer', padding:'9px 12px' }}>
                    <div className="flex-gap" style={{ gap:8, alignItems:'baseline' }}>
                      <span style={{ fontSize:12 }}>{ICON[g.key] || '•'}</span>
                      <span style={{ fontSize:13, fontWeight:600 }}>{it.title}</span>
                    </div>
                    {it.sub && <div style={{ fontSize:11.5, color:'var(--text2)', marginLeft:20 }}>{it.sub}</div>}
                  </button>
                  {it.copy && (
                    <button onClick={e => copy(e, it.copy)} title={`Copy ${it.copy}`}
                      style={{ flexShrink:0, marginRight:8, padding:'6px 10px', fontSize:11.5,
                        fontWeight:600, background:'var(--bg3)', border:'1px solid var(--border)',
                        borderRadius:'var(--radius)', cursor:'pointer',
                        color: copied === it.copy ? 'var(--green)' : 'var(--text2)' }}>
                      {copied === it.copy ? '✓ Copied' : 'Copy'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
